/**
 * CompanyMarketingHub — private marketing library for Cloud GM FZ LLC.
 *
 * Route (via /marketing?platform=company): rendered inline by Marketing.js.
 *
 * Tabs:
 *   • phone       → mobile-number ads (card creation, no image)
 *   • plate       → car-plate ads (uses UAEPlate)
 *   • car         → car ads (image gallery + spec sheet + PDF via jsPDF)
 *   • real_estate → real-estate ads (image gallery + spec sheet + PDF via jsPDF)
 *
 * Data isolation: written to db_admin.company_marketing_items — NEVER read
 * by customer/merchant platforms.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Phone, Car, Building2, Hash, Plus, Loader2, Search, Download,
  FileText, X, ChevronLeft, Eye, Printer, Image as ImageIcon,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { getAuthHeaders } from '../../utils/authHeaders';
import UAEPlate, { hasPlateDesign } from '../../components/plates/UAEPlate';
import CompanyMarketingEditor from './CompanyMarketingEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'phone',       icon: Phone,      color: 'blue',    labelAr: 'أرقام الموبايل',   labelEn: 'Phone Numbers' },
  { id: 'plate',       icon: Hash,       color: 'amber',   labelAr: 'لوحات السيارات',   labelEn: 'Car Plates' },
  { id: 'car',         icon: Car,        color: 'emerald', labelAr: 'السيارات',          labelEn: 'Cars' },
  { id: 'real_estate', icon: Building2,  color: 'purple',  labelAr: 'العقارات',          labelEn: 'Real Estate' },
];

export default function CompanyMarketingHub({ language, onBack }) {
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [tab, setTab] = useState('phone');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { mode: 'create'|'edit'|'view', type, item? }
  const [autoPrint, setAutoPrint] = useState(null); // { format, item }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ type: tab });
      if (q) params.set('q', q);
      const res = await axios.get(
        `${API_URL}/api/admin/company-marketing?${params.toString()}`,
        { headers: getAuthHeaders() },
      );
      setItems(res.data?.data || []);
      setCounts(res.data?.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [tab, q, isAr, t]);

  useEffect(() => { load(); }, [load]);

  const del = async (item) => {
    if (!window.confirm(t('حذف نهائي؟', 'Delete permanently?'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/company-marketing/${item.id}`,
        { headers: getAuthHeaders() });
      toast.success(t('✓ تم الحذف', '✓ Deleted'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  // Editor page (create / edit / view)
  if (editing) {
    return (
      <CompanyMarketingEditor
        language={language}
        mode={editing.mode}
        type={editing.type}
        item={editing.item}
        onClose={(saved) => {
          setEditing(null);
          if (saved) load();
        }}
      />
    );
  }

  // iter-208 — hidden auto-print (no visible editor, silent download & close).
  if (autoPrint) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#050505', zIndex: 50,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-white/70 text-sm inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('جاري إنشاء الملف…', 'Generating file…')}
        </div>
        <div style={{ position: 'absolute', top: '-10000px', left: '-10000px', opacity: 0 }} aria-hidden>
          <CompanyMarketingEditor
            language={language}
            mode="view"
            type={autoPrint.item.type}
            item={autoPrint.item}
            autoExport={autoPrint.format}
            onClose={() => setAutoPrint(null)}
          />
        </div>
      </div>
    );
  }

  const activeTabConfig = TABS.find(x => x.id === tab);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="company-marketing-hub">
      {/* Back + header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70"
          data-testid="company-marketing-back"
        >
          <ChevronLeft className={`h-5 w-5 ${isAr ? 'rotate-180' : ''}`} />
        </button>
        <h2 className="text-xl font-bold text-white flex-1">
          {t('تسويق Cloud GM — مكتبة خاصة بالشركة', 'Cloud GM Marketing — Company Library')}
        </h2>
        <Button
          onClick={() => setEditing({ mode: 'create', type: tab })}
          className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold"
          data-testid="company-marketing-add-btn"
        >
          <Plus className="h-4 w-4 me-1" />
          {t(`إضافة ${activeTabConfig?.labelAr}`, `Add ${activeTabConfig?.labelEn}`)}
        </Button>
      </div>

      {/* Tabs — always 4 */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {TABS.map((tabItem) => {
          const Icon = tabItem.icon;
          const isActive = tab === tabItem.id;
          const count = counts[tabItem.id] || 0;
          return (
            <button
              key={tabItem.id}
              onClick={() => setTab(tabItem.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition ${
                isActive
                  ? `bg-${tabItem.color}-500/20 border-${tabItem.color}-500/50 text-${tabItem.color}-300`
                  : 'bg-white/[0.02] border-white/10 text-white/60 hover:bg-white/5 hover:text-white'
              }`}
              data-testid={`company-marketing-tab-${tabItem.id}`}
            >
              <Icon className="h-4 w-4" />
              {isAr ? tabItem.labelAr : tabItem.labelEn}
              {count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-white/25 text-white' : `bg-${tabItem.color}-500/25 text-${tabItem.color}-300`
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" style={isAr ? { right: 10 } : { left: 10 }} />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('بحث في العناوين أو الوسوم', 'Search titles or tags')}
          className={`bg-[#050505] border-white/10 text-white text-sm ${isAr ? 'pr-9' : 'pl-9'}`}
          data-testid="company-marketing-search"
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-white/[0.02] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white/[0.02] rounded-2xl border border-white/10">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-${activeTabConfig?.color}-500/10 border border-${activeTabConfig?.color}-500/20`}>
            {(() => {
              const Icon = activeTabConfig?.icon;
              return Icon ? <Icon className={`w-8 h-8 text-${activeTabConfig?.color}-400`} /> : null;
            })()}
          </div>
          <p className="text-lg text-white">{t('لا توجد عناصر بعد', 'No items yet')}</p>
          <p className="text-sm mt-2 text-white/50">
            {t(`اضغط "إضافة ${activeTabConfig?.labelAr}" لبدء المكتبة`, `Click "Add ${activeTabConfig?.labelEn}" to start`)}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              isAr={isAr}
              onView={() => setEditing({ mode: 'view', type: item.type, item })}
              onPrint={(format) => setAutoPrint({ format, item })}
            />
          ))}
        </div>
      )}
      {/* del retained but only used internally now — remove reference from ItemCard */}
    </div>
  );
}

/* ─────────────────────── Card component ─────────────────────── */

function ItemCard({ item, isAr, onView, onPrint }) {
  const t = (ar, en) => (isAr ? ar : en);
  const [printMenuOpen, setPrintMenuOpen] = useState(false);

  // Print formats available per type
  const formats = useMemo(() => {
    if (item.type === 'phone' || item.type === 'plate') {
      return [
        { id: 'story', labelAr: 'Story (1080×1920)', labelEn: 'Story (1080×1920)', icon: ImageIcon },
        { id: 'post',  labelAr: 'Post (1080×1080)',  labelEn: 'Post (1080×1080)',  icon: ImageIcon },
      ];
    }
    // car / real_estate
    return [
      { id: 'story', labelAr: 'Story (1080×1920)', labelEn: 'Story (1080×1920)', icon: ImageIcon },
      { id: 'post',  labelAr: 'Post (1080×1080)',  labelEn: 'Post (1080×1080)',  icon: ImageIcon },
      { id: 'pdf',   labelAr: 'PDF (A4)',           labelEn: 'PDF (A4)',           icon: FileText },
    ];
  }, [item.type]);

  const renderPreview = () => {
    if (item.type === 'phone') {
      return (
        <div className="h-32 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/[0.02] border border-blue-500/20 flex flex-col items-center justify-center p-4">
          <Phone className="h-6 w-6 text-blue-400 mb-2" />
          <p className="text-white font-mono text-lg tracking-wider" dir="ltr">
            {item.data?.number || '—'}
          </p>
          {item.data?.operator && (
            <p className="text-blue-300/80 text-xs mt-1">{item.data.operator}</p>
          )}
        </div>
      );
    }

    if (item.type === 'plate') {
      const emirate = item.data?.emirate;
      const category = item.data?.category || 'private';
      if (emirate && hasPlateDesign(emirate, category)) {
        return (
          <div className="h-32 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
            <UAEPlate
              emirate={emirate}
              category={category}
              code={item.data?.code}
              number={item.data?.number}
              width={280}
            />
          </div>
        );
      }
      return (
        <div className="h-32 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
          <Hash className="h-8 w-8 text-amber-400" />
        </div>
      );
    }

    // car / real_estate — show first image
    const firstImg = item.images?.[0];
    if (firstImg) {
      return (
        <div className="h-32 rounded-xl overflow-hidden bg-white/5 border border-white/10">
          <img src={firstImg} alt={item.title} className="w-full h-full object-cover" />
        </div>
      );
    }
    return (
      <div className={`h-32 rounded-xl flex items-center justify-center ${
        item.type === 'car'
          ? 'bg-emerald-500/10 border border-emerald-500/20'
          : 'bg-purple-500/10 border border-purple-500/20'
      }`}>
        {item.type === 'car'
          ? <Car className="h-8 w-8 text-emerald-400" />
          : <Building2 className="h-8 w-8 text-purple-400" />}
      </div>
    );
  };

  return (
    <div
      className="p-4 bg-white/[0.02] rounded-2xl border border-white/10 hover:border-[#d4af37]/40 transition"
      data-testid={`company-marketing-card-${item.id}`}
    >
      {renderPreview()}
      <div className="mt-3">
        <h3 className="font-bold text-white text-sm truncate">{item.title}</h3>
        {item.tags?.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {item.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded-md text-[9px] bg-white/5 text-white/60">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 relative">
        <p className="text-[10px] text-white/30 font-mono">
          {item.created_at ? new Date(item.created_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US') : ''}
        </p>
        <div className="flex items-center gap-1">
          {/* Print dropdown — iter-208 */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setPrintMenuOpen((v) => !v); }}
              className="p-1.5 rounded-md bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
              title={t('طباعة', 'Print')}
              data-testid={`company-marketing-print-${item.id}`}
            >
              <Printer className="h-3.5 w-3.5" />
            </button>
            {printMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setPrintMenuOpen(false)} />
                <div
                  className={`absolute z-50 bottom-full mb-2 ${isAr ? 'start-0' : 'end-0'}
                              min-w-[180px] bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl overflow-hidden`}
                  data-testid={`company-marketing-print-menu-${item.id}`}
                >
                  {formats.map((f) => {
                    const FIcon = f.icon;
                    return (
                      <button
                        key={f.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPrintMenuOpen(false);
                          onPrint(f.id);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-start text-[11px] text-white/80"
                        data-testid={`company-marketing-print-${item.id}-${f.id}`}
                      >
                        <FIcon className="w-3.5 h-3.5 text-emerald-300" />
                        <span>{isAr ? f.labelAr : f.labelEn}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* View (eye) */}
          <button
            onClick={onView}
            className="px-2 py-1 rounded-md bg-white/5 text-white/70 hover:bg-white/10 text-[10px] font-medium flex items-center gap-1"
            data-testid={`company-marketing-view-${item.id}`}
          >
            <Eye className="w-3.5 h-3.5" />
            {t('عرض', 'View')}
          </button>
        </div>
      </div>
    </div>
  );
}
